import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { StoreIcon, UserIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useReturns } from '@/api/returns'
import type { Return } from '@/api/returns'
import { useSalesChannels } from '@/api/sales-channels'
import { VoidedBadge } from '@/components/corrections/voided'
import { FilterBar } from '@/components/filter-bar'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { formatBusinessDate } from '@/components/opening-balances/format'
import { RecordLink, RecordList, RecordListEmpty, RecordListItem } from '@/components/record-list'
import { Button } from '@/components/ui/button'

// Returns list (UI-SCREENS I-07, 05d #172): date, Sales Channel, units, who recorded it and the
// voided badge, filterable by channel and date. Owner and Staff both see it and both may add one
// (permissions.md). The filters sit in the shared filter bar (as on I-03) and live in the URL, so
// a filtered list survives a reload and can be linked to.
const searchSchema = z.object({
  channelId: z.coerce.number().int().positive().optional().catch(undefined),
  from: z.iso.date().optional().catch(undefined),
  to: z.iso.date().optional().catch(undefined),
})

export const Route = createFileRoute('/w/$slug/returns/')({
  component: ReturnsList,
  validateSearch: searchSchema,
  staticData: { breadcrumb: 'returns.title' },
})

function ReturnsList() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const channels = useSalesChannels(slug)
  const filters = { salesChannelId: search.channelId, from: search.from, to: search.to }
  const returns = useReturns(slug, filters)
  const filtered = Object.values(filters).some((value) => value !== undefined)

  function set(patch: Partial<typeof search>) {
    navigate({ search: (current) => ({ ...current, ...patch }), replace: true })
  }

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-xl font-semibold">{t('returns.title')}</h1>
        <Button asChild className="h-11">
          <Link to="/w/$slug/returns/new" params={{ slug }}>
            {t('returns.new')}
          </Link>
        </Button>
      </div>

      <FilterBar
        fields={[
          {
            key: 'channelId',
            label: t('returns.columns.channel'),
            icon: StoreIcon,
            value: search.channelId ? String(search.channelId) : undefined,
            options: (channels.data ?? []).map((channel) => ({ value: String(channel.id), label: channel.name })),
            onChange: (value) => set({ channelId: value === undefined ? undefined : Number(value) }),
          },
        ]}
        date={{ from: search.from, to: search.to }}
        onDateChange={(range) => set({ from: range.from, to: range.to })}
        onClearAll={() => set({ channelId: undefined, from: undefined, to: undefined })}
      />

      {returns.isPending ? (
        <LoadingSkeleton />
      ) : returns.isError || !returns.data ? (
        <p role="alert" className="text-sm text-destructive">
          {t('errors.generic')}
        </p>
      ) : returns.data.length === 0 ? (
        <RecordListEmpty>{t(filtered ? 'returns.emptyFiltered' : 'returns.empty')}</RecordListEmpty>
      ) : (
        <RecordList>
          {returns.data.map((document) => (
            <ReturnRow key={document.id} slug={slug} document={document} />
          ))}
        </RecordList>
      )}
    </section>
  )
}

function ReturnRow({ slug, document }: { slug: string; document: Return }) {
  const { t } = useTranslation()

  return (
    <RecordListItem
      voided={Boolean(document.voided_at)}
      title={
        <RecordLink to="/w/$slug/returns/$returnId" params={{ slug, returnId: String(document.id) }}>
          {document.return_no}
        </RecordLink>
      }
      badges={<VoidedBadge record={document} />}
      meta={[
        { icon: StoreIcon, label: t('returns.columns.channel'), value: document.sales_channel.name },
        document.created_by
          ? {
              icon: UserIcon,
              label: t('returns.columns.createdBy'),
              value: document.created_by.name ?? document.created_by.email,
            }
          : null,
      ]}
      figure={t('returns.units', { count: document.total_units })}
      caption={formatBusinessDate(document.occurred_on)}
    />
  )
}
